import { ExperienceListDto, ExperienceTestType } from "@intelligems/ig-types";

export const isSelectedTestTypeNotCreated = (
  experiences: ExperienceListDto[],
  campaigns: ExperienceListDto[],
  selectedTestType: ExperienceTestType
): boolean => {
  switch (selectedTestType) {
    case "offer":
      return (
        campaigns.length === 0 &&
        experiences.filter((exp) => exp.testTypes.hasTestCampaign).length === 0
      );
    case "pricing":
      return (
        experiences.filter((exp) => exp.testTypes.hasTestPricing).length === 0
      );
    case "shipping":
      return (
        experiences.filter((exp) => exp.testTypes.hasTestShipping).length === 0
      );
    case "content":
      return (
        experiences.filter((exp) => exp.testTypes.hasTestContent).length === 0
      );
    default:
      return false;
  }
};

export const filterByTestType = (
  experiencesList: ExperienceListDto[],
  selectedTestType: ExperienceTestType
) => {
  return experiencesList.filter((exp) =>
    selectedTestType === "offer"
      ? exp.testTypes.hasTestCampaign
      : selectedTestType === "pricing"
        ? exp.testTypes.hasTestPricing
        : selectedTestType === "shipping"
          ? exp.testTypes.hasTestShipping
          : exp.testTypes.hasTestContent
  );
};

export const previewModeLink = (
  experienceTestList: ExperienceListDto[],
  campaigns: ExperienceListDto[],
  selectedTestType: ExperienceTestType,
  domain: string
): boolean | string => {
  const canPreview = !isSelectedTestTypeNotCreated(
    experienceTestList,
    campaigns,
    selectedTestType
  );

  if (canPreview) {
    if (selectedTestType === "offer") {
      const campaignExperiments = filterByTestType(
        experienceTestList,
        selectedTestType
      );
      let newestCampaignExperiment: ExperienceListDto | undefined = undefined;
      let newestCampaign: ExperienceListDto | undefined = undefined;
      if (campaignExperiments.length > 0) {
        newestCampaignExperiment = campaignExperiments.reduce(
          (min: ExperienceListDto, experiment: ExperienceListDto) =>
            experiment.createdAtTs > min.createdAtTs ? experiment : min,
          campaignExperiments[0]
        );
      }
      if (campaigns.length > 0) {
        newestCampaign = campaigns.reduce(
          (min: ExperienceListDto, campaign: ExperienceListDto) =>
            campaign.createdAtTs > min.createdAtTs ? campaign : min,
          campaigns[0]
        );
      }

      if (newestCampaignExperiment && newestCampaign) {
        return `${domain}/?ig-preview=${
          newestCampaign.createdAtTs >
          (newestCampaignExperiment?.createdAtTs || 0)
            ? newestCampaign.id
            : newestCampaignExperiment.id
        }`;
      } else if (newestCampaignExperiment) {
        return `${domain}/?ig-preview=${newestCampaignExperiment.id}`;
      } else if (newestCampaign) {
        return `${domain}/?ig-preview=${newestCampaign.id}`;
      } else return false;
    } else {
      const eligibleExperiments = filterByTestType(
        experienceTestList,
        selectedTestType
      );
      if (eligibleExperiments.length > 0) {
        const newestExperiment = eligibleExperiments.reduce(
          (min: ExperienceListDto, experiment: ExperienceListDto) =>
            experiment.createdAtTs > min.createdAtTs ? experiment : min,
          eligibleExperiments[0]
        );
        if (newestExperiment) {
          return `${domain}/?ig-preview=${newestExperiment.id}`;
        } else return false;
      } else return false;
    }
  } else return false;
};

export const getNewestEndedTest = <
  T extends Pick<
    ExperienceListDto,
    "status" | "startedAtTs" | "endedAtTs" | "experienceAnalysis"
  >,
>(
  experiences: T[]
): T | undefined => {
  const endedExperiences = experiences.filter(
    (experience: T) => experience.status === "ended"
  );

  if (endedExperiences.length > 0) {
    return endedExperiences.reduce(
      (max: T, experience: T) =>
        experience.endedAtTs! > max.endedAtTs! ? experience : max,
      endedExperiences[0]
    );
  } else return undefined;
};

export const getOldestStartedTest = <
  T extends Pick<
    ExperienceListDto,
    "status" | "startedAtTs" | "endedAtTs" | "experienceAnalysis"
  >,
>(
  experiences: T[]
) => {
  const activeExperiences = getRunningExperiences(experiences);
  if (activeExperiences.length > 0) {
    return activeExperiences.reduce(
      (max: T, experience: T) =>
        experience.startedAtTs! < max.startedAtTs! ? experience : max,
      activeExperiences[0]
    );
  } else return undefined;
};

export const getRunningExperiences = <
  T extends Pick<
    ExperienceListDto,
    "status" | "startedAtTs" | "endedAtTs" | "experienceAnalysis"
  >,
>(
  experiences: T[],
  hasImpactData?: boolean
) => {
  return experiences.filter((experience: T) =>
    hasImpactData
      ? experience.status === "started" &&
        experience?.experienceAnalysis?.impact
      : experience.status === "started"
  );
};
